import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Botao,
  Campo,
  CampoDeTexto,
  MenuDeAcoes,
  Modal,
  Selo,
  useDialogo,
} from '../../componentes/ui';
import {
  ativarCaptadores,
  captadoresDoCard,
  criarCaptador,
  editarCaptador,
  excluirCaptador,
  type CaptadorDoCrm,
  type CardDetalhado,
  type TagDoCrm,
} from '../../servicos/crm';
import { ErroDaApi } from '../ErroDaApi';

/**
 * Captadores do card, por tag: quem trouxe a empresa para cada serviço e com
 * que participação.
 *
 * Cada tag do card tem o seu interruptor. Desligado, nada aparece — a maioria
 * dos negócios não tem captador, e o card não deve carregar um formulário
 * vazio por isso. Desligar não apaga os captadores: religar os traz de volta.
 */
export function CaptadoresDoCard(props: {
  readonly card: CardDetalhado;
  readonly tags: readonly TagDoCrm[];
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const [formulario, setFormulario] = useState<{
    tagId: string;
    captador?: CaptadorDoCrm;
  } | null>(null);

  const consulta = useQuery({
    queryKey: ['crm', 'captadores', props.card.id],
    queryFn: () => captadoresDoCard(props.card.id),
  });
  const alternar = useMutation({
    mutationFn: (m: { tagId: string; ligado: boolean }) =>
      ativarCaptadores(props.card.id, m.tagId, m.ligado),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['crm'] }),
  });

  const tagsDoCard = props.tags.filter((t) => props.card.tagIds.includes(t.id));
  if (tagsDoCard.length === 0) return null;

  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-conteudo-sutil">
        Captadores
      </h3>
      {alternar.isError && (
        <ErroDaApi titulo="Não foi possível alterar os captadores" erro={alternar.error} />
      )}
      <ul className="flex flex-col gap-3">
        {tagsDoCard.map((tag) => {
          const ligado = props.card.tagsComCaptadores.includes(tag.id);
          const daTag = (consulta.data ?? []).filter((c) => c.tagId === tag.id);
          const total = daTag.reduce((soma, c) => soma + (c.percentual ?? 0), 0);
          return (
            <li key={tag.id} className="flex flex-col gap-3 rounded-card border border-borda p-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Selo tom={tag.cor} tamanho="pequeno" semPonto>
                  {tag.nome}
                </Selo>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-conteudo-secundario">
                  <input
                    type="checkbox"
                    checked={ligado}
                    disabled={alternar.isPending}
                    onChange={(e) => alternar.mutate({ tagId: tag.id, ligado: e.target.checked })}
                    className="h-4 w-4 rounded border-borda bg-superficie-base text-acento-ciano"
                  />
                  Ativar captadores
                </label>
              </div>

              {ligado && (
                <>
                  {daTag.length === 0 ? (
                    <p className="text-xs text-conteudo-sutil">Nenhum captador cadastrado.</p>
                  ) : (
                    <ul className="flex flex-col divide-y divide-borda/60">
                      {daTag.map((c) => (
                        <LinhaDoCaptador
                          key={c.id}
                          captador={c}
                          aoEditar={() => setFormulario({ tagId: tag.id, captador: c })}
                        />
                      ))}
                    </ul>
                  )}
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Botao
                      variante="secundario"
                      tamanho="pequeno"
                      onClick={() => setFormulario({ tagId: tag.id })}
                    >
                      + Adicionar captador
                    </Botao>
                    {daTag.length > 0 && (
                      <span
                        className={`text-xs tabular-nums ${total > 100 ? 'font-medium text-erro' : 'text-conteudo-sutil'}`}
                      >
                        Participação total: {total.toLocaleString('pt-BR')}%
                        {total > 100 && ' — passa de 100%'}
                      </span>
                    )}
                  </div>
                </>
              )}
            </li>
          );
        })}
      </ul>

      {formulario !== null && (
        <FormularioDoCaptador
          cardId={props.card.id}
          tagId={formulario.tagId}
          {...(formulario.captador === undefined ? {} : { captador: formulario.captador })}
          aoFechar={() => setFormulario(null)}
        />
      )}
    </section>
  );
}

function LinhaDoCaptador(props: {
  readonly captador: CaptadorDoCrm;
  readonly aoEditar: () => void;
}): JSX.Element {
  const c = props.captador;
  const queryClient = useQueryClient();
  const dialogo = useDialogo();
  const apagar = useMutation({
    mutationFn: () => excluirCaptador(c.id),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['crm'] }),
  });

  return (
    <li className="flex items-start justify-between gap-3 py-2.5">
      <div className="flex min-w-0 flex-col gap-0.5 text-sm">
        <span className="font-medium text-conteudo-primario">
          {c.nome}
          {c.percentual !== null && (
            <span className="ml-2 font-normal tabular-nums text-conteudo-secundario">
              {c.percentual.toLocaleString('pt-BR')}%
            </span>
          )}
        </span>
        {(c.documento ?? c.contato) !== null && (
          <span className="text-xs text-conteudo-secundario">
            {[c.documento, c.contato].filter((x) => x !== null).join(' · ')}
          </span>
        )}
        {c.dadosBancarios !== null && (
          <span className="whitespace-pre-wrap text-xs text-conteudo-sutil">
            Dados bancários: {c.dadosBancarios}
          </span>
        )}
        {c.observacoes !== null && (
          <span className="whitespace-pre-wrap text-xs text-conteudo-sutil">{c.observacoes}</span>
        )}
        {apagar.isError && <ErroDaApi titulo="Não foi possível excluir" erro={apagar.error} />}
      </div>
      <MenuDeAcoes
        rotulo={c.nome}
        itens={[
          { rotulo: 'Editar', aoEscolher: props.aoEditar },
          {
            rotulo: 'Excluir',
            perigo: true,
            aoEscolher: async () => {
              const ok = await dialogo.confirmar({
                titulo: `Excluir o captador ${c.nome}?`,
                rotuloConfirmar: 'Excluir',
                tom: 'perigo',
              });
              if (ok) apagar.mutate();
            },
          },
        ]}
      />
    </li>
  );
}

/** Campos livres: é anotação da operação, não cadastro validado. Só o nome é obrigatório. */
function FormularioDoCaptador(props: {
  readonly cardId: string;
  readonly tagId: string;
  readonly captador?: CaptadorDoCrm;
  readonly aoFechar: () => void;
}): JSX.Element {
  const c = props.captador;
  const queryClient = useQueryClient();
  const [nome, setNome] = useState(c?.nome ?? '');
  const [documento, setDocumento] = useState(c?.documento ?? '');
  const [contato, setContato] = useState(c?.contato ?? '');
  const [percentual, setPercentual] = useState(
    c?.percentual === null || c?.percentual === undefined
      ? ''
      : String(c.percentual).replace('.', ','),
  );
  const [dadosBancarios, setDadosBancarios] = useState(c?.dadosBancarios ?? '');
  const [observacoes, setObservacoes] = useState(c?.observacoes ?? '');

  const numero = percentual.trim() === '' ? null : Number(percentual.replace(',', '.'));
  const percentualValido =
    numero === null || (Number.isFinite(numero) && numero >= 0 && numero <= 100);

  const salvar = useMutation({
    mutationFn: () => {
      const dados = {
        nome: nome.trim(),
        documento: documento.trim() || null,
        contato: contato.trim() || null,
        percentual: numero === null ? null : Math.round(numero * 100) / 100,
        dadosBancarios: dadosBancarios.trim() || null,
        observacoes: observacoes.trim() || null,
      };
      return c === undefined
        ? criarCaptador(props.cardId, props.tagId, dados)
        : editarCaptador(c.id, dados);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['crm'] });
      props.aoFechar();
    },
  });

  const valido = nome.trim() !== '' && percentualValido;

  return (
    <Modal
      aberto
      aoFechar={props.aoFechar}
      titulo={c === undefined ? 'Novo captador' : 'Editar captador'}
      rodape={
        <>
          <Botao variante="secundario" onClick={props.aoFechar}>
            Cancelar
          </Botao>
          <Botao disabled={!valido} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
            Salvar
          </Botao>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (valido) salvar.mutate();
        }}
      >
        <Campo
          rotulo="Nome do captador"
          obrigatorio
          autoFocus
          maxLength={200}
          value={nome}
          onChange={(e) => setNome(e.target.value)}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            rotulo="CPF ou CNPJ"
            maxLength={30}
            value={documento}
            onChange={(e) => setDocumento(e.target.value)}
          />
          <Campo
            rotulo="Participação (%)"
            inputMode="decimal"
            placeholder="Ex.: 10 ou 12,5"
            value={percentual}
            onChange={(e) => setPercentual(e.target.value)}
            {...(percentualValido ? {} : { erro: 'Um número de 0 a 100.' })}
          />
        </div>
        <Campo
          rotulo="Contato"
          ajuda="Telefone, e-mail — o que for útil."
          maxLength={300}
          value={contato}
          onChange={(e) => setContato(e.target.value)}
        />
        <CampoDeTexto
          rotulo="Dados bancários"
          ajuda="Banco, agência, conta, PIX."
          rows={2}
          value={dadosBancarios}
          onChange={(e) => setDadosBancarios(e.target.value)}
        />
        <CampoDeTexto
          rotulo="Observações"
          rows={2}
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
        />
        {salvar.isError && <ErroDaApi titulo="Não foi possível salvar" erro={salvar.error} />}
        <button type="submit" className="sr-only">
          Salvar
        </button>
      </form>
    </Modal>
  );
}
